import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { legacyApi, Customer, SaleOrder } from '../../services/legacyApi';
import { api } from '../../services/api';
import type { Account, ARReceiptMode, ArOpenInvoice, AllocationLine } from '../../services/types';
import { toast } from 'sonner';
import { RequiredLabel } from '../../components/forms/RequiredLabel';
import { FieldErrorText } from '../../components/forms/FieldErrorText';
import { showValidationSummaryToast } from '../../components/forms/ValidationSummaryToast';
import { focusFirstInvalidField, validateRequiredFields } from '../../components/forms/formValidationUtils';
import { FlowEntryBanner } from '../../components/workflow/FlowEntryBanner';
import { useNavigate, useSearchParams } from 'react-router-dom';

type ReceiptMode = Extract<ARReceiptMode, 'INVOICE_SETTLEMENT' | 'CUSTOMER_PREPAYMENT'>;

const MODE_LABELS: Record<ReceiptMode, string> = {
    INVOICE_SETTLEMENT: 'Invoice Settlement',
    CUSTOMER_PREPAYMENT: 'Customer Prepayment',
};

export function ARReceiptForm() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [selectedCustomer, setSelectedCustomer] = useState<string>('');
    const [saleOrders, setSaleOrders] = useState<SaleOrder[]>([]);
    const [selectedSoId, setSelectedSoId] = useState<string>('');
    const [cashAccounts, setCashAccounts] = useState<Account[]>([]);
    const [selectedCashAccountId, setSelectedCashAccountId] = useState<string>('');
    const [mode, setMode] = useState<ReceiptMode>('INVOICE_SETTLEMENT');
    const [openInvoices, setOpenInvoices] = useState<ArOpenInvoice[]>([]);
    const [allocationDraft, setAllocationDraft] = useState<Record<number, string>>({});
    const [amount, setAmount] = useState<number>(0);
    const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [reference, setReference] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [lastPost, setLastPost] = useState<{ receipt_id?: number; journal_id?: number; mode?: string } | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const prefillCustomerId = searchParams.get('customer_id');
    const prefillInvoiceId = searchParams.get('invoice_id');
    const prefillAmount = searchParams.get('amount');
    const prefillMode = searchParams.get('mode');

    useEffect(() => {
        legacyApi.customers.list().then(setCustomers);
        api.financials
            .listAccounts()
            .then((rows) => {
                const candidates = rows.filter((account) => {
                    const label = `${account.code || ''} ${account.name || ''}`.toLowerCase();
                    return (
                        account.type === 'Asset'
                        && account.is_active !== false
                        && account.is_postable !== false
                        && (label.includes('cash') || label.includes('bank') || account.code === '1000' || account.parent_account_id === 1000)
                    );
                });
                setCashAccounts(candidates);
            })
            .catch(() => setCashAccounts([]));
    }, []);

    useEffect(() => {
        if (prefillMode === 'CUSTOMER_PREPAYMENT') {
            setMode('CUSTOMER_PREPAYMENT');
        } else if (prefillMode === 'INVOICE_SETTLEMENT') {
            setMode('INVOICE_SETTLEMENT');
        }
    }, [prefillMode]);

    useEffect(() => {
        if (!prefillCustomerId || selectedCustomer) return;
        setSelectedCustomer(prefillCustomerId);
    }, [prefillCustomerId, selectedCustomer]);

    useEffect(() => {
        if (!prefillAmount || amount > 0) return;
        const parsed = Number(prefillAmount);
        if (Number.isFinite(parsed) && parsed > 0) {
            setAmount(Number(parsed.toFixed(2)));
        }
    }, [prefillAmount, amount]);

    useEffect(() => {
        if (!selectedCustomer) {
            setSaleOrders([]);
            setSelectedSoId('');
            setOpenInvoices([]);
            setAllocationDraft({});
            return;
        }

        const customer = customers.find(c => c.customer_id === Number(selectedCustomer));
        if (!customer) {
            return;
        }

        legacyApi.sales.list().then(orders => {
            const filtered = orders.filter(o => o.customer_name === customer.customer_name && o.so_status_id !== 7);
            setSaleOrders(filtered);
        });
    }, [selectedCustomer, customers]);

    useEffect(() => {
        if (mode !== 'INVOICE_SETTLEMENT' || !selectedCustomer) {
            setOpenInvoices([]);
            setAllocationDraft({});
            return;
        }
        api.financials.listArOpenInvoices(Number(selectedCustomer))
            .then(rows => {
                setOpenInvoices(rows);
                setAllocationDraft({});
            })
            .catch((error: any) => {
                console.error(error);
                toast.error(error?.message || 'Failed to load open invoices');
            });
    }, [selectedCustomer, mode]);

    useEffect(() => {
        if (!prefillInvoiceId || openInvoices.length === 0) return;
        const targetInvoiceId = Number(prefillInvoiceId);
        if (!Number.isFinite(targetInvoiceId)) return;
        const match = openInvoices.find(inv => inv.invoice_id === targetInvoiceId);
        if (!match) return;
        setAllocationDraft((prev) => {
            if (prev[targetInvoiceId]) return prev;
            const apply = amount > 0 ? Math.min(amount, match.outstanding_amount) : match.outstanding_amount;
            return { ...prev, [targetInvoiceId]: apply.toFixed(2) };
        });
    }, [prefillInvoiceId, openInvoices, amount]);

    const allocationLines = useMemo<AllocationLine[]>(() => {
        return openInvoices
            .map(inv => {
                const value = parseFloat(allocationDraft[inv.invoice_id] ?? '');
                if (!Number.isFinite(value) || value <= 0) return null;
                return { document_id: inv.invoice_id, apply_amount: Number(value.toFixed(2)) };
            })
            .filter((line): line is AllocationLine => !!line);
    }, [allocationDraft, openInvoices]);

    const allocatedTotal = useMemo(() => {
        return Number(allocationLines.reduce((sum, line) => sum + line.apply_amount, 0).toFixed(2));
    }, [allocationLines]);

    const autoAllocate = () => {
        let remaining = Number(amount || 0);
        if (!Number.isFinite(remaining) || remaining <= 0) {
            return;
        }
        const next: Record<number, string> = {};
        openInvoices.forEach(inv => {
            if (remaining <= 0) return;
            const apply = Math.min(inv.outstanding_amount, remaining);
            if (apply > 0) {
                next[inv.invoice_id] = apply.toFixed(2);
                remaining = Number((remaining - apply).toFixed(2));
            }
        });
        setAllocationDraft(next);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const validation = validateRequiredFields([
            { key: 'selectedCustomer', label: 'Customer', value: selectedCustomer, required: true },
            { key: 'selectedCashAccountId', label: 'Cash/Bank Account', value: selectedCashAccountId, required: true },
            { key: 'date', label: 'Date', value: date, required: true },
            { key: 'amount', label: 'Amount', value: amount > 0 ? amount : '', required: true },
        ]);
        const nextErrors = { ...validation.errorsByField } as Record<string, string>;

        if (mode === 'INVOICE_SETTLEMENT') {
            if (allocationLines.length === 0) {
                nextErrors.allocations = 'At least one invoice allocation is required.';
            } else if (Math.abs(allocatedTotal - Number(amount || 0)) > 0.01) {
                nextErrors.allocations = 'Allocation total must exactly match receipt amount.';
            }
        }

        if (mode === 'CUSTOMER_PREPAYMENT' && selectedSoId) {
            // no-op, but keeps branch explicit for readability
        }

        if (!validation.valid || Object.keys(nextErrors).length > 0) {
            setFieldErrors(nextErrors);
            showValidationSummaryToast();
            focusFirstInvalidField(validation.firstInvalidField || 'allocations');
            return;
        }
        setFieldErrors({});

        setLoading(true);
        try {
            const result = await api.financials.postArReceipt({
                customer_id: parseInt(selectedCustomer, 10),
                mode,
                amount: Number(amount),
                date,
                reference,
                cash_account_id: parseInt(selectedCashAccountId, 10),
                so_id: mode === 'CUSTOMER_PREPAYMENT' && selectedSoId ? parseInt(selectedSoId, 10) : undefined,
                allocations: mode === 'INVOICE_SETTLEMENT' ? allocationLines : [],
            });
            setLastPost({
                receipt_id: Number(result?.receipt_id || 0),
                journal_id: Number(result?.journal_id || 0),
                mode: String(result?.mode || mode),
            });
            toast.success('Receipt posted successfully.');
            setAmount(0);
            setReference('');
            setAllocationDraft({});
            if (mode === 'INVOICE_SETTLEMENT' && selectedCustomer) {
                const rows = await api.financials.listArOpenInvoices(Number(selectedCustomer));
                setOpenInvoices(rows);
            }
        } catch (error: any) {
            console.error(error);
            toast.error(error?.message || 'Failed to post receipt');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto space-y-3">
            <FlowEntryBanner
                title="AR Receipt Flow"
                pathLabel="Invoice -> Receipt Allocation"
                nextHint="Record receipts against invoices for full settlement traceability."
                helpModule="finance"
            />
            <Card>
                <CardHeader>
                    <CardTitle>Customer Payment Receipt</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
                        Finance impact: Debit Cash/Bank. Credit AR for invoice settlements, or credit Unearned Revenue for prepayments.
                    </div>
                    <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                        Receipt document output is available after posting. Once a receipt is saved, this screen will show
                        ` View Receipt `, ` Preview / Print `, and ` Download PDF ` actions for that posted receipt.
                    </div>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                                <RequiredLabel required htmlFor="selectedCustomer">Customer</RequiredLabel>
                                <select
                                    id="selectedCustomer"
                                    name="selectedCustomer"
                                    aria-invalid={!!fieldErrors.selectedCustomer}
                                    aria-required="true"
                                    className="w-full p-2 border rounded"
                                    value={selectedCustomer}
                                    onChange={(e) => {
                                        setSelectedCustomer(e.target.value);
                                        setSelectedSoId('');
                                    }}
                                    required
                                >
                                    <option value="">Select Customer</option>
                                    {customers.map(c => (
                                        <option key={c.customer_id} value={c.customer_id}>{c.customer_name}</option>
                                    ))}
                                </select>
                                <FieldErrorText message={fieldErrors.selectedCustomer} />
                            </div>
                            <div className="space-y-2">
                                <RequiredLabel required htmlFor="mode">Mode</RequiredLabel>
                                <select
                                    id="mode"
                                    name="mode"
                                    className="w-full p-2 border rounded"
                                    value={mode}
                                    onChange={(e) => setMode(e.target.value as ReceiptMode)}
                                >
                                    <option value="INVOICE_SETTLEMENT">{MODE_LABELS.INVOICE_SETTLEMENT}</option>
                                    <option value="CUSTOMER_PREPAYMENT">{MODE_LABELS.CUSTOMER_PREPAYMENT}</option>
                                </select>
                            </div>
                        </div>

                        {mode === 'CUSTOMER_PREPAYMENT' && (
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Link to Sale Order (Optional)</label>
                                <select
                                    className="w-full p-2 border rounded"
                                    value={selectedSoId}
                                    onChange={(e) => setSelectedSoId(e.target.value)}
                                    disabled={!selectedCustomer}
                                >
                                    <option value="">-- General Customer Prepayment --</option>
                                    {saleOrders.map(so => (
                                        <option key={so.so_id} value={so.so_id}>
                                            SO #{so.so_id} - {so.estimate_ref || 'N/A'} ({so.status_name})
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-muted-foreground">
                                    Prepayment remains unapplied until allocated to one or more posted invoices.
                                </p>
                            </div>
                        )}

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                                <RequiredLabel required htmlFor="date">Date</RequiredLabel>
                                <input
                                    id="date"
                                    name="date"
                                    aria-invalid={!!fieldErrors.date}
                                    aria-required="true"
                                    type="date"
                                    className="w-full p-2 border rounded"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    required
                                />
                                <FieldErrorText message={fieldErrors.date} />
                            </div>
                            <div className="space-y-2">
                                <RequiredLabel required htmlFor="amount">Amount</RequiredLabel>
                                <input
                                    id="amount"
                                    name="amount"
                                    aria-invalid={!!fieldErrors.amount}
                                    aria-required="true"
                                    type="number"
                                    className="w-full p-2 border rounded"
                                    value={amount}
                                    onChange={(e) => setAmount(parseFloat(e.target.value || '0'))}
                                    required
                                    min="0.01"
                                    step="0.01"
                                />
                                <FieldErrorText message={fieldErrors.amount} />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium">Reference / Remarks</label>
                            <input
                                type="text"
                                className="w-full p-2 border rounded"
                                value={reference}
                                onChange={(e) => setReference(e.target.value)}
                                placeholder="Cheque No, bank transfer ref, remarks"
                            />
                        </div>

                        <div className="space-y-2">
                            <RequiredLabel required htmlFor="selectedCashAccountId">Cash/Bank Account</RequiredLabel>
                            <select
                                id="selectedCashAccountId"
                                name="selectedCashAccountId"
                                aria-invalid={!!fieldErrors.selectedCashAccountId}
                                aria-required="true"
                                className="w-full p-2 border rounded"
                                value={selectedCashAccountId}
                                onChange={(e) => setSelectedCashAccountId(e.target.value)}
                                required
                            >
                                <option value="">Select cash/bank account</option>
                                {cashAccounts.map((account) => (
                                    <option key={account.id} value={account.id}>
                                        {account.code} - {account.name}
                                    </option>
                                ))}
                            </select>
                            <FieldErrorText message={fieldErrors.selectedCashAccountId} />
                        </div>

                        {mode === 'INVOICE_SETTLEMENT' && (
                            <div className="space-y-2 rounded-md border p-3">
                                <div className="flex items-center justify-between gap-3">
                                    <p className="text-sm font-medium">Invoice Allocations</p>
                                    <Button type="button" variant="outline" size="sm" onClick={autoAllocate} disabled={openInvoices.length === 0}>
                                        Auto Allocate
                                    </Button>
                                </div>
                                {openInvoices.length === 0 ? (
                                    <p className="text-xs text-muted-foreground">No open invoices found for this customer.</p>
                                ) : (
                                    <div className="max-h-56 overflow-auto rounded border">
                                        <table className="w-full text-sm">
                                            <thead className="bg-muted/40">
                                                <tr>
                                                    <th className="p-2 text-left font-medium">Invoice</th>
                                                    <th className="p-2 text-right font-medium">Outstanding</th>
                                                    <th className="p-2 text-right font-medium">Apply</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {openInvoices.map(inv => (
                                                    <tr key={inv.invoice_id} className="border-t">
                                                        <td className="p-2">{inv.invoice_no}</td>
                                                        <td className="p-2 text-right">{inv.outstanding_amount.toFixed(2)}</td>
                                                        <td className="p-2">
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                max={inv.outstanding_amount}
                                                                className="w-full rounded border p-1 text-right"
                                                                value={allocationDraft[inv.invoice_id] ?? ''}
                                                                onChange={(e) =>
                                                                    setAllocationDraft(prev => ({
                                                                        ...prev,
                                                                        [inv.invoice_id]: e.target.value,
                                                                    }))
                                                                }
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                <div className="flex items-center justify-between text-xs">
                                    <span>Allocated: {allocatedTotal.toFixed(2)}</span>
                                    <span>Difference: {(Number(amount || 0) - allocatedTotal).toFixed(2)}</span>
                                </div>
                                <FieldErrorText message={fieldErrors.allocations} />
                            </div>
                        )}

                        <Button type="submit" disabled={loading} className="w-full">
                            {loading ? 'Posting...' : `Post ${MODE_LABELS[mode]}`}
                        </Button>
                    </form>
                    {lastPost && (
                        <div className="mt-4 space-y-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
                            <div>
                                Posted successfully. Receipt #{lastPost.receipt_id || '-'} | Journal #{lastPost.journal_id || '-'} | Mode {lastPost.mode || mode}
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => lastPost.receipt_id && navigate(`/financials/receipts/${lastPost.receipt_id}`)}
                                    disabled={!lastPost.receipt_id}
                                >
                                    View Receipt
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => lastPost.receipt_id && api.financials.openArReceiptPrint(lastPost.receipt_id, false)}
                                    disabled={!lastPost.receipt_id}
                                >
                                    Preview
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={async () => {
                                        if (!lastPost.receipt_id) return;
                                        try {
                                            await api.financials.downloadArReceiptPdf(lastPost.receipt_id);
                                        } catch (error: any) {
                                            toast.error(error?.message || 'Failed to download receipt PDF');
                                        }
                                    }}
                                    disabled={!lastPost.receipt_id}
                                >
                                    PDF
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
